import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = 'https://groovie-business-api-production.up.railway.app/api/v1';

export default function BroadcastStatus() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restarting, setRestarting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const token = localStorage.getItem('token');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/broadcast/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) throw new Error('Failed to fetch status');
      const data = await res.json();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchStatus();
    if (autoRefresh) {
      const interval = setInterval(fetchStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [fetchStatus, autoRefresh]);

  const handleRestart = async () => {
    if (!window.confirm('Restart the broadcast scheduler?')) return;
    setRestarting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/broadcast/restart`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTimeout(fetchStatus, 2000);
    } catch (err) {
      alert('Restart failed: ' + err.message);
    } finally {
      setRestarting(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleString();
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const diff = new Date(iso) - new Date();
    if (diff < 0) return 'overdue';
    if (diff < 60000) return `in ${Math.round(diff / 1000)}s`;
    if (diff < 3600000) return `in ${Math.round(diff / 60000)}m`;
    return `in ${Math.round(diff / 3600000)}h`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Broadcast Status</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time monitoring of the autonomous broadcast engine</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="rounded" />
            Auto-refresh (10s)
          </label>
          <button onClick={fetchStatus} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Refresh</button>
          <button onClick={handleRestart} disabled={restarting} className="px-3 py-2 text-sm bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50">
            {restarting ? 'Restarting...' : 'Restart Scheduler'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      <div className={`mb-6 p-4 rounded-xl border-2 ${status?.running ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-4 h-4 rounded-full ${status?.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <div>
              <span className="font-semibold text-lg">{status?.running ? 'Engine Running' : 'Engine Stopped'}</span>
              {status?.started_at && <p className="text-sm text-gray-600">Started: {formatTime(status.started_at)}</p>}
            </div>
          </div>
          <div className="text-right text-sm text-gray-600">{status?.jobs?.length || 0} active jobs</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Programs Switched', value: status?.stats?.programs_switched || 0, icon: '\ud83d\udcfb' },
          { label: 'Announcements Played', value: status?.stats?.announcements_played || 0, icon: '\ud83d\udce2' },
          { label: 'Events Triggered', value: status?.stats?.events_triggered || 0, icon: '\ud83d\udcc5' },
          { label: 'Ads Played', value: status?.stats?.ads_played || 0, icon: '\ud83d\udce3' },
          { label: 'News Fetched', value: status?.stats?.news_fetched || 0, icon: '\ud83d\udcf0' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl mb-1">{stat.icon}</div>
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
            <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">Scheduled Jobs</h2>
          <div className="space-y-3">
            {status?.jobs?.length > 0 ? status.jobs.map(job => (
              <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm text-gray-900">{job.name}</p>
                  <p className="text-xs text-gray-500">{job.id}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-purple-600">{formatRelative(job.next_run)}</p>
                  <p className="text-xs text-gray-400">{formatTime(job.next_run)}</p>
                </div>
              </div>
            )) : <p className="text-gray-400 text-sm text-center py-4">No jobs scheduled</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">Active Programs</h2>
          <div className="space-y-3">
            {status?.active_programs && Object.keys(status.active_programs).length > 0 ? (
              Object.entries(status.active_programs).map(([chId, prog]) => (
                <div key={chId} className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-purple-900">{prog.name}</p>
                      <p className="text-xs text-purple-600">Channel #{chId} - {prog.content_type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      <span className="text-xs text-green-700 font-medium">LIVE</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Started: {formatTime(prog.started_at)}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-400 text-sm">No programs currently active</p>
                <p className="text-xs text-gray-300 mt-1">Programs activate per their day/time schedule</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {status?.recent_errors?.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border border-red-200 p-5">
          <h2 className="text-lg font-semibold mb-4 text-red-700">Recent Errors</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {status.recent_errors.map((err, i) => (
              <div key={i} className="flex items-start gap-3 p-2 bg-red-50 rounded text-sm">
                <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5">{formatTime(err.time)}</span>
                <span className="text-red-700">{err.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 bg-gray-50 rounded-xl border border-gray-200 p-5">
        <h2 className="text-lg font-semibold mb-3 text-gray-700">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
          <div>
            <p className="font-medium text-gray-800 mb-1">Every 60 seconds</p>
            <p>Checks radio programs, announcement frequencies, calendar events, and ad campaign schedules.</p>
          </div>
          <div>
            <p className="font-medium text-gray-800 mb-1">Every hour</p>
            <p>Fetches latest industry news via RSS feeds based on your business category.</p>
          </div>
          <div>
            <p className="font-medium text-gray-800 mb-1">Daily at midnight</p>
            <p>Processes recurring calendar events and advances them to their next occurrence.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
